/**
 * The right-hand bar's headed lists and the Ask box, as the Synthesis V3 spec's screen shows them.
 *
 * The spec names the entries but gives them no destinations, so they are labels, not links: no
 * chevrons, nothing in the tab order but the group headers, which only fold their own list. Several
 * entries name pages that do not exist yet (Company Profile, Transcripts, Report Library, Notes,
 * Methodology); wiring the ones that do is a later change once the spec says where each goes. The
 * Ask box is likewise inert — there is no question-answering service behind it yet, and so is Time
 * Machine (Goals UI V4 item 21), which has no historical view to open.
 */

import type { ComponentType } from 'react';
import { useState } from 'react';

import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import InputBase from '@mui/material/InputBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import {
  IconAffiliate,
  IconBooks,
  IconChartBar,
  IconChevronDown,
  IconChevronRight,
  IconClockHour4,
  IconFileText,
  IconHierarchy2,
  IconId,
  IconInfoCircle,
  IconLayoutDashboard,
  IconMicrophone,
  IconNotes,
  IconSearch,
  IconSettings,
  IconTable,
  IconUsers
} from '@tabler/icons-react';

type Entry = { label: string; Icon: ComponentType<{ size?: number; stroke?: number }> };

const GROUPS: { title: string; entries: Entry[] }[] = [
  {
    title: 'Key Information',
    entries: [
      { label: 'Company Profile', Icon: IconId },
      { label: 'Documents', Icon: IconFileText },
      { label: 'Financial Statements', Icon: IconChartBar },
      { label: 'Transcripts', Icon: IconMicrophone },
      { label: 'Report Library', Icon: IconBooks },
      { label: 'Notes', Icon: IconNotes }
    ]
  },
  {
    title: 'Explore',
    entries: [
      { label: 'Cognitives', Icon: IconAffiliate },
      { label: 'Goals', Icon: IconHierarchy2 },
      { label: 'Synthesis', Icon: IconLayoutDashboard },
      { label: 'FINSETs', Icon: IconTable }
    ]
  },
  {
    title: 'Project Resources',
    entries: [
      { label: 'Team', Icon: IconUsers },
      { label: 'Search', Icon: IconSearch },
      { label: 'Methodology', Icon: IconInfoCircle },
      { label: 'Settings', Icon: IconSettings }
    ]
  }
];

const headingSx = (theme: Theme) => ({
  color: theme.vars.palette.primary[800],
  ...theme.applyStyles('dark', { color: theme.vars.palette.text.primary })
});

export function BarLists() {
  return (
    <Stack spacing={1.5}>
      {GROUPS.map((group) => (
        <Group key={group.title} {...group} />
      ))}
    </Stack>
  );
}

function Group({ title, entries }: { title: string; entries: Entry[] }) {
  const [open, setOpen] = useState(true);
  const id = `bar-group-${title.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <Box sx={{ pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
      <ButtonBase
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={id}
        sx={{ width: '100%', justifyContent: 'space-between', py: 0.75, px: 0.5, borderRadius: 1 }}
      >
        <Typography variant="subtitle1" sx={(theme) => ({ fontWeight: 700, ...headingSx(theme) })}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', color: 'text.secondary' }}>{open ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}</Box>
      </ButtonBase>
      <Collapse in={open}>
        <Box component="ul" id={id} sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {entries.map(({ label, Icon }) => (
            <Stack component="li" key={label} direction="row" alignItems="center" spacing={1.25} sx={{ py: 0.6, px: 0.5 }}>
              <Box sx={{ display: 'flex', color: 'text.secondary' }}>
                <Icon size={18} stroke={1.6} />
              </Box>
              <Typography variant="body2" color="text.primary">
                {label}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

export function AskBox() {
  return (
    <Box>
      <Typography variant="subtitle1" sx={(theme) => ({ fontWeight: 700, mb: 0.75, ...headingSx(theme) })}>
        Ask ntab.io
      </Typography>
      <InputBase
        disabled
        fullWidth
        placeholder="Ask a question…"
        inputProps={{ 'aria-label': 'Ask a question (not available yet)' }}
        endAdornment={
          <Box sx={{ display: 'flex', color: 'text.disabled' }}>
            <IconChevronRight size={16} />
          </Box>
        }
        sx={{ px: 1.5, py: 0.75, borderRadius: 1.5, bgcolor: 'background.paper', fontSize: '0.875rem' }}
      />
    </Box>
  );
}

/** The rail's last entry, under FACE. A label like the lists above: there is nowhere to go yet. */
export function TimeMachine() {
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 0.5 }}>
      <Box sx={{ display: 'flex', color: 'text.secondary' }}>
        <IconClockHour4 size={20} stroke={1.6} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" sx={(theme) => ({ fontWeight: 700, lineHeight: 1.3, ...headingSx(theme) })}>
          Time Machine
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Explore historical analysis
        </Typography>
      </Box>
    </Stack>
  );
}
